import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

export default function DashboardPage({ setActivePage }) {
  const { currentUser, updateUser, logoutUser } = useAuth();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: currentUser?.name || '',
    phone: currentUser?.phone || '',
    password: currentUser?.password || ''
  });
  const [successMsg, setSuccessMsg] = useState('');

  if (!currentUser) {
    return (
      <div className="max-w-md mx-auto py-16 text-center space-y-4">
        <h2 className="text-2xl font-bold text-slate-900">Session Required</h2>
        <p className="text-slate-600 text-sm">Please log in or register to access your user dashboard.</p>
        <div className="flex justify-center gap-3">
          <button
            onClick={() => setActivePage('login')}
            className="bg-blue-600 text-white px-5 py-2.5 rounded-xl font-semibold text-sm"
          >
            Sign In
          </button>
          <button
            onClick={() => setActivePage('register')}
            className="bg-slate-800 text-white px-5 py-2.5 rounded-xl font-semibold text-sm"
          >
            Registration Page
          </button>
        </div>
      </div>
    );
  }

  const handleUpdate = (e) => {
    e.preventDefault();
    updateUser(currentUser.id, {
      name: formData.name,
      phone: formData.phone,
      password: formData.password
    });
    setSuccessMsg('Profile updated successfully!');
    setIsEditing(false);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-slate-900 to-indigo-900 rounded-3xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center space-x-5">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-tr from-blue-500 to-indigo-500 flex items-center justify-center font-bold text-3xl shadow-inner">
            {currentUser.name.charAt(0).toUpperCase()}
          </div>
          <div className="space-y-1">
            <div className="inline-flex items-center space-x-1.5 bg-indigo-500/30 text-indigo-200 px-3 py-0.5 rounded-full text-xs font-semibold">
              <span>{currentUser.role}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">{currentUser.name}</h1>
            <p className="text-slate-300 text-sm">{currentUser.email}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {currentUser.role === 'Admin' && (
            <button
              onClick={() => setActivePage('management')}
              className="bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-4 py-2.5 rounded-xl text-sm transition-colors shadow"
            >
              Reg Management
            </button>
          )}
          <button
            onClick={() => {
              logoutUser();
              setActivePage('home');
            }}
            className="bg-slate-800 hover:bg-rose-600 text-slate-200 hover:text-white font-semibold px-4 py-2.5 rounded-xl text-sm transition-colors border border-slate-700"
          >
            Logout
          </button>
        </div>
      </div>

      {successMsg && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-xl text-sm">
          {successMsg}
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Left: Profile Information */}
        <div className="md:col-span-2 bg-white rounded-3xl p-8 shadow-sm border border-slate-200 space-y-6">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4">
            <h2 className="text-lg font-bold text-slate-900">Account Profile Details</h2>
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="text-xs font-semibold text-blue-600 hover:underline"
            >
              {isEditing ? 'Cancel Edit' : 'Edit Profile'}
            </button>
          </div>

          {isEditing ? (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                  Phone Number
                </label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-sm"
                />
              </div>
              <div className="pt-2 flex space-x-3">
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-5 py-2.5 rounded-xl text-sm"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="bg-slate-200 hover:bg-slate-300 text-slate-800 font-semibold px-5 py-2.5 rounded-xl text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
              <div className="space-y-1">
                <span className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Full Name</span>
                <p className="font-semibold text-slate-800 text-base">{currentUser.name}</p>
              </div>
              <div className="space-y-1">
                <span className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Email Address</span>
                <p className="font-semibold text-slate-800 text-base">{currentUser.email}</p>
              </div>
              <div className="space-y-1">
                <span className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Phone Number</span>
                <p className="font-semibold text-slate-800 text-base">{currentUser.phone}</p>
              </div>
              <div className="space-y-1">
                <span className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Account Status</span>
                <p className="font-semibold text-emerald-600 text-base flex items-center space-x-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  <span>{currentUser.status || 'Active'}</span>
                </p>
              </div>
              <div className="space-y-1">
                <span className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Registration Date</span>
                <p className="font-semibold text-slate-800 text-sm">
                  {new Date(currentUser.createdAt || Date.now()).toLocaleDateString()}
                </p>
              </div>
              <div className="space-y-1">
                <span className="text-slate-400 text-xs uppercase tracking-wider font-semibold">User ID</span>
                <p className="font-mono text-slate-600 text-xs">{currentUser.id}</p>
              </div>
            </div>
          )}
        </div>

        {/* Right: Quick Navigation & Prototype Info */}
        <div className="space-y-6">
          <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-200 space-y-4">
            <h3 className="font-bold text-slate-900 text-base">Quick Actions</h3>
            <div className="space-y-2">
              <button
                onClick={() => setActivePage('register')}
                className="w-full text-left px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-xl text-sm font-medium text-slate-700 flex items-center justify-between"
              >
                <span>Registration Page</span>
                <span>→</span>
              </button>
              <button
                onClick={() => setActivePage('management')}
                className="w-full text-left px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-xl text-sm font-medium text-slate-700 flex items-center justify-between"
              >
                <span>Registration Management</span>
                <span>→</span>
              </button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-3xl p-6 text-blue-900 space-y-2">
            <h4 className="font-bold text-sm">Session Active</h4>
            <p className="text-xs text-blue-800 leading-relaxed">
              You are logged in as a registered prototype user. Your session data is safely preserved across browser reloads via LocalStorage.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
